import {
  ArchivedDocument,
  CollectionReference,
  DocumentArchive,
  DocumentReference,
  Firestore,
  IReffable,
  subCollection,
  WithRef,
} from '@principle-theorem/shared';
import {
  IAIReceptionistSettings,
  IPractice,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import { Practice } from '@principle-theorem/principle-core';
import { omit } from 'lodash';

export class AIReceptionistSettingsService {
  /**
   * Get the current Ovie Receptionist Settings for a practice.
   * Returns undefined if no settings document exists.
   */
  static async getSettings(
    practice: IReffable<IPractice>
  ): Promise<WithRef<IAIReceptionistSettings> | undefined> {
    return Firestore.firstResult(Practice.aiReceptionistSettingsCol(practice));
  }

  /**
   * Save or update the Ovie Receptionist Settings for a practice.
   * If settings already exist, archives the current version before saving.
   * Never writes the sync-owned fields (`sectionEtags`, `lastPushStatus`,
   * `lastPushAt`): only the Ovie settings sync may change them, and the
   * caller's copy can be stale. An update therefore patches only the content,
   * so a sync write landing between the read and the save survives. The
   * caller's copy is usually the loaded document, so its `ref` is dropped too.
   */
  static async saveSettings(
    practice: IReffable<IPractice>,
    settings: IAIReceptionistSettings,
    stafferRef?: DocumentReference<IStaffer>
  ): Promise<DocumentReference<IAIReceptionistSettings>> {
    const settingsContent = omit(
      settings,
      'ref',
      'sectionEtags',
      'lastPushStatus',
      'lastPushAt'
    );
    const existing = await AIReceptionistSettingsService.getSettings(practice);

    if (existing) {
      await DocumentArchive.snapshotToArchive(
        existing,
        AIReceptionistSettingsService.archiveCol(existing),
        undefined,
        stafferRef
      );
      await Firestore.patchDoc(existing.ref, settingsContent);
      return existing.ref;
    }

    return Firestore.addDoc(
      Practice.aiReceptionistSettingsCol(practice),
      settingsContent
    );
  }

  /**
   * Archive subcollection for an Ovie Receptionist Settings document.
   */
  static archiveCol(
    settingsDoc: WithRef<IAIReceptionistSettings>
  ): CollectionReference<ArchivedDocument<IAIReceptionistSettings>> {
    return subCollection<ArchivedDocument<IAIReceptionistSettings>>(
      settingsDoc.ref,
      'archive'
    );
  }
}
